import { fetchEntityIfNeeded } from '@classpass/saga-tools';
import * as logger from 'universal-logger';
import { all, call, select } from 'redux-saga/effects';
import { userPermissions, userProfile, userSubscription } from 'redux/saga/entities/bound';
import { isLoggedIn } from 'redux/saga/selectors';
import { gtmSafeRedirect } from 'redux/saga/helpers';
import { deepExtract } from 'helpers/deep';
import loginRedirectUrl from 'helpers/loginRedirectUrl';
import { memberStatus } from 'enums/memberStatus';
import { SCREEN_TYPES } from 'constants/media-queries';
import { HOW_TO_CANCEL } from 'constants/helpdeskLinks';
import { selectDeviceType, selectQuery } from 'redux/modules/request';
import queryString from 'query-string';
import { COMMITMENT_PLAN_IDS } from 'constants/temporaryPlanIds';
import { ROUTES } from './Settings.constants';

export function* settingsOnEnterSaga(client, nextState, replaceState) {
  if (!(yield select(isLoggedIn))) {
    yield call(replaceState, loginRedirectUrl(nextState.location));
  }
  // Currently nothing lives at /settings, so we redirect to membership as the default
  if (nextState.location.pathname === ROUTES.settings) {
    yield call(replaceState, ROUTES.membership);
  }
}

export function* updateOnEnterSaga(client, replaceState) {
  try {
    const profile = yield call(fetchEntityIfNeeded, userProfile, [client]);
    const subscription = yield call(fetchEntityIfNeeded, userSubscription, [client, profile.id]);

    const plan = deepExtract('current_subscription.plan', subscription);
    if (COMMITMENT_PLAN_IDS.includes(plan && plan.id)) {
      const [deviceType, queryVars] = yield all([select(selectDeviceType), select(selectQuery)]);

      const isMobile = [SCREEN_TYPES.phone, SCREEN_TYPES.tablet].includes(deviceType);
      const isWebview = !!queryVars.webview;

      if (isMobile && !isWebview) {
        return yield call(gtmSafeRedirect, HOW_TO_CANCEL);
      }

      const qs = queryString.stringify(queryVars);
      return yield call(replaceState, `/settings/membership/update/cancel${qs ? `?${qs}` : ''}`);
    }
  } catch (err) {
    yield call(logger.warn, 'Settings: Could not load updateOnEnterSaga', err);
  }
}

export function* plansOnEnterSaga(client, replaceState, nextState) {
  try {
    const profile = yield call(fetchEntityIfNeeded, userProfile, [client]);

    if (!memberStatus('ACTIVE', profile && profile.membership && profile.membership.status)) {
      return yield call(replaceState, '/settings');
    }

    const subscription = yield call(fetchEntityIfNeeded, userSubscription, [client, profile.id]);

    const plan = deepExtract('current_subscription.plan', subscription);
    if (COMMITMENT_PLAN_IDS.includes(plan && plan.id)) {
      const searchString = deepExtract('location.search', nextState, '');
      return yield call(replaceState, `/settings/membership${searchString}`);
    }

    if (!subscription || !subscription.allow_plan_selection) {
      yield call(replaceState, '/settings');
    }

    if (subscription && subscription.current_subscription && subscription.current_subscription.plan) {
      if (subscription.current_subscription.plan.commitment) {
        yield call(replaceState, '/settings/membership');
      }
    }
  } catch (err) {
    yield call(logger.warn, 'Settings: Could not load plansOnEnterSaga', err);
  }
}

export function* addOnsOnEnterSaga(client, replaceState, nextState) {
  try {
    const profile = yield call(fetchEntityIfNeeded, userProfile, [client]);
    const permissions = yield call(fetchEntityIfNeeded, userPermissions, [client, profile.id]);

    if (!memberStatus('ACTIVE', profile && profile.membership && profile.membership.status)) {
      yield call(replaceState, '/settings');
    } else if (!permissions.class_bundles) {
      const searchString = deepExtract('location.search', nextState, '');
      yield call(replaceState, `/settings/membership${searchString}`);
    }
  } catch (err) {
    yield call(logger.warn, 'Settings: Could not load addOnsOnEnterSaga', err);
  }
}
